import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import { ContentChange, QuillEditorComponent, QuillModules } from 'ngx-quill';
import QuillType, { Delta } from 'quill';

import { BehaviorSubject, Subscription, first, startWith, switchMap } from 'rxjs';
import { SubSink } from 'subsink';

import { QuillTemplate } from '../../../../generated/graphql.generated';
import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { QuillTemplateVariableToolbarDirective } from '../../../shared/quill-template-variable.directive';
import { brandingFeature } from '../../../state/branding.store';
import { generateUUID } from '../../../utilities/state.util';
import { WorkOrdersActions } from '../../job-state/workorders-state/workorders.actions';
import { JobToolActions } from '../../job-tool.actions';
import { jobToolFeature } from '../../job-tool.reducer';
import { CommentChange } from '../../jobv2-create/jobv2-interfaces';
import { commentDefaultToolbar, isQuillEmpty } from '../comments.utils';
import { COMMENT_MODES_TEMPLATES, JOB_TEMPLATE_VARIABLES } from '../quill-modules/quill-templates.helpers';
import { VariableToolbarQuillModule } from '../quill-modules/variableToolbar.quillModule';

@Component({
  selector: 'app-job-add-comment',
  standalone: true,
  imports: [
    CommonModule,
    QuillEditorComponent,
    FormsModule,
    FreyaCommonModule,
    QuillTemplateVariableToolbarDirective
  ],
  templateUrl: './job-add-comment.component.html',
  styleUrl: './job-add-comment.component.scss'
})
export class JobAddCommentComponent implements OnInit, OnDestroy {
  @ViewChild(QuillEditorComponent, { static: true }) editor: QuillEditorComponent | undefined;
  @ViewChild('subjectEditor', { static: false }) subjectEditor: QuillEditorComponent | undefined;

  mutating$ = this.store.select(jobToolFeature.addCommentLoading);
  commentModes$ = this.store.select(brandingFeature.selectCommentModes);
  job$ = this.store.select(jobToolFeature.selectJob);

  jobTemplateVariablesData$ = this.store.select(jobToolFeature.selectJobTemplateVariablesData);

  currentCommentMode$ = this.store.select(createSelector(
    brandingFeature.selectCommentModes,
    jobToolFeature.selectCurrentCommentMode,
    (modes, currentCommentMode) => {

      const mode = modes?.find((m) => m.id === currentCommentMode);
      return mode || {
        id: 'Comment',
        name: 'Comment',
      };
    },
  ));

  commentModeMenuItems$ = this.store.select(createSelector(
    brandingFeature.selectCommentModes,
    jobToolFeature.selectCurrentCommentMode,
    (modes, currentCommentMode) => {
      if (!modes?.length) {
        modes = [{
          id: 'Comment',
          name: 'Comment',
        }];
      }

      return modes
        .filter((m) => !m?.hidden)
        .map((m) => ({
          ...m,
          label: m.name,
          visible: m.id !== currentCommentMode,
          tooltip: m.description,
          tooltipOptions: {
            tooltipPosition: 'left',
          },
          // disabled: allDisabled,
          command: () => this.store.dispatch(JobToolActions.commentModeSet({
            mode: m.id,
          })),
        }));
    },
  ));

  quillTemplatesAndVariables$ = this.store.select(createSelector(
    brandingFeature.selectTemplates,
    jobToolFeature.selectCurrentCommentMode,
    (templates = [], currentCommentMode) => {
      const templateLocation = COMMENT_MODES_TEMPLATES[currentCommentMode];
      const quillTemplates = templates.filter((t) => t.location === templateLocation);
      return {
        templates: quillTemplates,
        variables: JOB_TEMPLATE_VARIABLES,
      };
    }
 ));


  subs = new SubSink();

  unsavedCommentsSubscription: Subscription;

  newCommentContent = {};

  hasPermission = true;

  modules = {
    toolbar: commentDefaultToolbar,

    variableToolbar: true,
    templateToolbar: true,

    keyboard: {
      bindings: {
        submit: {
          key: 'Enter',
          ctrlKey: true,
          handler: (range, context) => {
            this.addComment();
          },
        },
      },
    },
    // imageHandler: {
    //   upload: async (file: File) => {
    //     const artifact = await lastValueFrom(this.jobCommentArtifactHandlerService.uploadFile(file));
    //     console.log(artifact);

        // make the URL for the comment based on the current url
        // const url = new URL(location.href);

        // // clear everything but the zone
        // const zone = url.searchParams.get('zone');
        // url.search = '';
        // url.searchParams.set('zone', zone);

        // // set the path
        // url.pathname = `/api/artifact/${ artifact.id }`;

        // console.log(url.toString());

        // return url.toString();

    //     return `/api/artifact/${ artifact.id }`;
    //   }
    // },
  };


  // closeJobCommentLabel = 'Close Job';
  error: string;

  commentChanges$ = new BehaviorSubject<CommentChange[]>([]);

  public subjectModules: QuillModules = {
    toolbar: false,
    variableToolbar: { hideVariableButton: true},
  };

  constructor(
    private store: Store,
    private actions: Actions,
  ) { }

  ngOnInit(): void {

    this.subs.sink = this.store.select(jobToolFeature.selectCommentChanges).pipe(
        first(),
        switchMap((comments) => this.actions.pipe(
            ofType(WorkOrdersActions.applyUnsavedChangesFromLS),
            startWith({ unsavedCommentChanges: comments }),
        )),
    ).subscribe(({ unsavedCommentChanges }) => {
        this.commentChanges$.next(unsavedCommentChanges);
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();

    if (this.unsavedCommentsSubscription) {
      this.unsavedCommentsSubscription.unsubscribe();
    }
  }

  contentChanged(change: ContentChange) {
    if (change.source !== 'user') { return; }
    this.error = undefined;
    this.store.dispatch(JobToolActions.commentContentsChanged({
      content: change.content,
      text: change.text,
    }));
  }

  onEditorCreated(editor: QuillType) {

    // this.watchQuillTemplatesAndVariables();
    // this.watchTemplateVariablesData();

    if (this.unsavedCommentsSubscription) { return; }

    this.unsavedCommentsSubscription = this.commentChanges$
      .subscribe((changes) => {

        const change = changes.find((c) => !c.threadId && !c.id);

        if (change) {
          editor.setContents(change.content);
          return;
        }

        if (isQuillEmpty(editor)) {
          return;
        }

        editor.setContents([]);
      });
  }

  canAddComment() {
    // if (!this.jobId) return false;
    if (this.isEmpty()) return false;
    if (!this.hasPermission) return false;
    return true;
  }

  addComment() {
    if (!this.canAddComment()) { return false; }

    const variableToolbar = this.editor?.quillEditor?.getModule('variableToolbar') as VariableToolbarQuillModule;
    const {contents, text} = variableToolbar?.getResolvedContentsAndText();

    const subjectVariableToolbar = this.subjectEditor?.quillEditor?.getModule('variableToolbar') as VariableToolbarQuillModule;
    const {text: subjectText} = subjectVariableToolbar?.getResolvedContentsAndText() || {};

    const id = generateUUID();
    const threadId = generateUUID();
    this.store.dispatch(JobToolActions.addComment({
      input: {
        id,
        threadId,
        text,
        contents: JSON.stringify(contents),
        subject: subjectText,
      },
      isReply: false,
    }));
    this.clearContent();
    return true;
  }

  clearContent() {
    this.editor?.quillEditor?.setContents([{ insert: '\n' }]);
    this.subjectEditor?.quillEditor?.setContents([{ insert: '\n' }]);
  }

  isEmpty() {
    return isQuillEmpty(this.editor?.quillEditor);
  }

  onTemplateSelected = (template: QuillTemplate) => {
    // Update the subject editor with the template contents
    if (this.subjectEditor) {
        try {
            const delta = template.subjectContents ? JSON.parse(template.subjectContents) : new Delta();
            this.subjectEditor.quillEditor.setContents(delta, 'user');
        } catch (error) {
            console.error('Invalid Quill Delta JSON for subject:', error);
            alert('Error applying template to subject: Invalid format');
        }
    }

  };

//   watchTemplateVariablesData() {
//     this.subs.sink = this.jobTemplateVariablesData$.subscribe((templateVariablesData) => {
//         const variableToolbar = this.editor?.quillEditor?.getModule('variableToolbar') as VariableToolbarQuillModule;
//         variableToolbar.updateData(templateVariablesData);
//     });
//   }

//   watchQuillTemplatesAndVariables() {
//      this.subs.sink = this.quillTemplatesAndVariables$.subscribe(({templates, variables}) => {
//         const templateToolbar = this.editor?.quillEditor?.getModule('templateToolbar') as TemplateToolbarQuillModule;
//         const variableToolbar = this.editor?.quillEditor?.getModule('variableToolbar') as VariableToolbarQuillModule;

//         templateToolbar.updateTemplates(templates);
//         variableToolbar.updateVariables(variables);
//      });
//   }
}
